import { Location } from "@angular/common";
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";
import { EMPTY, Observable, of } from "rxjs";
import { catchError, switchMap, take, tap } from "rxjs/operators";
import { selectListingSummary } from "../listing-summary.selectors";
import { ListingSummary } from "../shared/interfaces/listing-summary";
import { ListingSummaryService } from "../shared/services/listing-summary.service";
import { SessionService } from "@recore-solutions/nstar-lib";
import { ConfigurationService } from "../configuration.service";

@Injectable({
	providedIn: "root",
})
export class ListingSummaryResolver {
	constructor(
		private listingSummaryService: ListingSummaryService,
		private router: Router,
		private location: Location,
		private sessionService: SessionService,
		private configService: ConfigurationService
	) {}

	resolve(
		route: ActivatedRouteSnapshot,
		routerStateSnapshot: RouterStateSnapshot
	): Observable<ListingSummary> {
		const listingID = route.params.mlid;
		const inStore$ = this.listingSummaryService.store.select(selectListingSummary).pipe(take(1));
		const inCloud$ = this.listingSummaryService.getByKey(listingID);
		const listingSummary$ = inStore$.pipe(
			switchMap((storeListingSummary) =>
				!!storeListingSummary ? of(storeListingSummary) : inCloud$
			)
		);
		return listingSummary$.pipe(
			tap(this.deletedListing),
			catchError(this.redirect(routerStateSnapshot))
		);
	}

	private deletedListing = (listingSummary: ListingSummary) => {
		if (
			["ID", "WD", "D"].includes(listingSummary.listingStatusCD) &&
			!this.sessionService.permissionLevels.some((pl) =>
				this.configService.config.permissions.navigateToDeleted.includes(
					pl?.accessName?.toLocaleLowerCase()
				)
			)
		) {
			throw new Error("Cannot navigate to deleted listing!");
		}
	};
	private redirect = (routerStateSnapshot: RouterStateSnapshot) => () => {
		this.router
			.navigate(["error/404"], { skipLocationChange: true })
			.then(() => this.location.go(routerStateSnapshot.url));
		return EMPTY;
	};
}
